// src/pages/ProductListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft } from 'lucide-react';
import apiClient from '../api/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import ContentFilters from '../components/common/ContentFilters';

const ProductListPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // ✅ خواندن پارامترهای URL
  const type = queryParams.get('type');
  const isFreeParam = queryParams.get('is_free'); // "true" یا "false"

  // عنوان صفحه بر اساس نوع محصول
  let pageTitle = 'همه درس‌گفتارها';
  let metaTitle = 'همه محصولات | فرزانه';
  let metaDescription = 'بیشتر محصولات فرزانه شامل درس‌گفتارها، آثار مکتوب، ویدیوها و دوره‌های آموزشی مرتبط با فلسفه، روانشناسی و هنر زندگی.';

  if (type === 'LECTURE') {
    pageTitle = 'فایل‌های صوتی';
    metaTitle = 'درس‌گفتارها | فرزانه';
    metaDescription = 'مجموعه درس‌گفتارهای مصطفی ملکیان و دیگر اندیشمندان در حوزه فلسفه، اخلاق و هنر زندگی. جدیدترین جلسات و مطالب صوتی.';
  } else if (type === 'EBOOK') {
    pageTitle = 'آثار مکتوب';
    metaTitle = 'آثار مکتوب | فرزانه';
    metaDescription = 'کتاب‌ها و مقالات مکتوب از مصطفی ملکیان و دیگر نویسندگان. آثار تألیفی، ترجمه‌ها و مقدمه‌ها در موضوعات فلسفی و روانشناسی.';
  } else if (type === 'VIDEO_COURSE') {
    pageTitle = 'ویدیوها';
    metaTitle = 'ویدیوها | فرزانه';
    metaDescription = 'دوره‌های ویدیویی و محتوای تصویری با موضوع فلسفه، روانشناسی و مهارت‌های زندگی. برگرفته از آثار مصطفی ملکیان و دیگر اساتید.';
  }

  // ✅ اگر پارامتر is_free در URL وجود دارد، عنوان را تغییر بده
  if (isFreeParam === 'true') {
    pageTitle = 'محتوای رایگان';
    metaTitle = 'محتوای رایگان | فرزانه';
    metaDescription = 'همه محتواهای رایگان فرزانه شامل درس‌گفتارها، کتاب‌ها و ویدیوهای آموزشی در حوزه فلسفه و روانشناسی.';
  } else if (isFreeParam === 'false') {
    pageTitle = 'محتوای خریدنی';
    metaTitle = 'محتوای خریدنی | فرزانه';
    metaDescription = 'همه محتواهای خریدنی فرزانه شامل درس‌گفتارها، کتاب‌ها و دوره‌های آموزشی تخصصی در حوزه فلسفه و روانشناسی.';
  }

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    sortOrder: 'newest',
    isFree: isFreeParam === 'true' ? true : isFreeParam === 'false' ? false : null,
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '/products/list/';
      const params = {};
      if (type) params.type = type;
      
      // ✅ اصلاح: مقدار isFree را به درستی به API بفرست
      if (filters.isFree !== null) {
        params.is_free = filters.isFree; // true یا false
      }
      
      if (filters.sortOrder === 'newest') params.sort_by = 'newest';

      console.log("🔍 Sending request with params:", params); // برای دیباگ

      const response = await apiClient.get(url, { params });
      setProducts(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('خطا در بارگذاری محصولات');
    } finally {
      setLoading(false);
    }
  }, [type, filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';

  if (loading) {
    return <LoadingSpinner text={`در حال بارگذاری ${pageTitle}...`} />;
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={defaultOgImage} />
      </Helmet>

      <div className="sub-categories-page rtl container mx-auto p-0 pb-20 max-w-[460px] overflow-x-hidden bg-white">
        {/* Breadcrumb */}
        <div className="p-4 bg-white border-b border-gray-200">
          <nav className="text-[11px] font-light text-gray-500 mb-2 flex items-center space-x-reverse space-x-1">
            <Link to="/" className="hover:text-indigo-600 transition">خانه</Link>
            <ChevronLeft size={14} className="mt-0.5 text-gray-400" />
            <span className="text-gray-800 font-medium">{pageTitle}</span>
          </nav>
          <h1 className="text-2xl font-extrabold text-gray-800 truncate">
            {pageTitle}
          </h1>
        </div>

        {/* فیلترها (مرتب‌سازی و فیلتر رایگان) */}
        <ContentFilters filters={filters} setFilters={setFilters} />

        <div className="p-4 md:p-6">
          {/* نمایش محصولات */}
          <div className="products-section">
            {products.length === 0 ? (
              <div className="text-center text-gray-500 py-10">
                <p>
                  {filters.isFree === true
                    ? 'هیچ محصول رایگانی در این دسته یافت نشد.'
                    : filters.isFree === false
                    ? 'هیچ محصول خریدنی در این دسته یافت نشد.'
                    : 'متأسفانه هیچ محصولی برای نمایش یافت نشد.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {products.map((product) => (
                  <VerticalContentCard key={product.id || product.slug} data={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductListPage;